'use client';

import { type ComponentProps, type ReactElement, forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataLayer } from '@/hooks/use-data-layer';
import { buildDataLayerEcommerce } from '@/lib/analytics/data-layer-mappers';
import { createProductUrl } from '@/lib/product-utils';
import { formatMultipackWeightText, getSearchHitMultipackPackQty } from '@/lib/multipack';
import { useProductTileContext } from '@/components/product-tile/context';
import { useProductTileBasketLine } from '@/hooks/use-product-tile-basket-line';
import { ProductTilePriceWeight, useProductPriceWeightDisplay } from '@/components/product-utils';
import { ProductTilePriceLine } from '@/components/product-utils/tile-price-display';
import { formatEventDateLine, formatEventStartDateForTile, getEventTileFields } from '@/lib/product-event-tile';
import { ProductTileCustomHorizontalCard } from './product-tile-custom-horizontal-card';
import { ProductTileCustomVerticalCard } from './product-tile-custom-vertical-card';
import { ProductTileCustomVerticalEventCard } from './product-tile-custom-vertical-event-card';
import type { ProductTileCustomLayout } from './product-tile-custom-types';
import type { SCAPIShopperSearchSchemas } from 'src/types';
import { ProductTileCustomEventCard } from './product-tile-custom-event-card';
import { ExternalEventPrice } from '@/components/product-price-custom/external-event-price';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';
import { AnalyticsItemListContext, type AnalyticsItemList } from '@/providers/analytics-item-list-context';

export type { ProductTileCustomLayout } from './product-tile-custom-types';

export interface ProductTileCustomProps extends ComponentProps<'div'> {
    product: SCAPIShopperSearchSchemas['ProductSearchHit'];
    handleProductClick?: (product: SCAPIShopperSearchSchemas['ProductSearchHit']) => void;
    /** GA4 `item_list_id` for `select_item`, `add_to_cart`, `remove_from_cart` and `add_to_wishlist` from this tile. */
    analyticsItemListId?: string;
    /** GA4 `item_list_name` for `select_item`, `add_to_cart`, `remove_from_cart` and `add_to_wishlist` from this tile. */
    analyticsItemListName?: string;
    /** 0-based position in the list for GA4 `index`. */
    analyticsIndex?: number;
    imgAspectRatio?: number;
    layout?: ProductTileCustomLayout;
    basketControlsDisabled?: boolean;
    /**
     * Wishlist-only: render events with the horizontal card (same as courses)
     * instead of the overlay event card.
     */
    useHorizontalEventCard?: boolean;
}

const ProductTileCustom = forwardRef<HTMLDivElement, ProductTileCustomProps>((props, ref) => {
    const {
        className,
        product,
        handleProductClick,
        analyticsItemListId,
        analyticsItemListName,
        analyticsIndex,
        imgAspectRatio,
        layout = 'vertical',
        basketControlsDisabled = false,
        useHorizontalEventCard = false,
        ...divProps
    } = props;

    const dataLayer = useDataLayer();
    const commerceEnabled = useCommerceEnabled();
    const { config, t, currency } = useProductTileContext();

    const onProductClick = useCallback(
        (clickedProduct: SCAPIShopperSearchSchemas['ProductSearchHit']) => {
            if (analyticsItemListId && analyticsItemListName) {
                const trackingItems =
                    analyticsIndex !== undefined ? [{ ...clickedProduct, index: analyticsIndex }] : [clickedProduct];
                const trackingContext = {
                    affiliation: dataLayer.affiliation,
                    itemListId: analyticsItemListId,
                    itemListName: analyticsItemListName,
                    includeValue: false,
                };
                dataLayer.track({
                    event: 'select_item',
                    ecommerce: buildDataLayerEcommerce(trackingItems, trackingContext),
                    event_type: 'ga4_eec',
                });
            }
            handleProductClick?.(clickedProduct);
        },
        [analyticsIndex, analyticsItemListId, analyticsItemListName, dataLayer, handleProductClick]
    );
    const analyticsItemList = useMemo<AnalyticsItemList | null>(
        () =>
            analyticsItemListId && analyticsItemListName
                ? { itemListId: analyticsItemListId, itemListName: analyticsItemListName, index: analyticsIndex }
                : null,
        [analyticsIndex, analyticsItemListId, analyticsItemListName]
    );
    const withItemList = (tile: ReactElement) =>
        analyticsItemList ? (
            <AnalyticsItemListContext.Provider value={analyticsItemList}>{tile}</AnalyticsItemListContext.Provider>
        ) : (
            tile
        );
    const { i18n, t: tSearch } = useTranslation('search');
    const brand = product.representedProduct?.c_pimBrandName;
    const effectiveImgAspectRatio = imgAspectRatio ?? config.global.productListing.defaultProductTileImgAspectRatio;

    const isHorizontal = layout === 'horizontal';
    const isVerticalEvent = layout === 'vertical-event';
    const currencyCode = currency ?? config.commerce.sites?.[0]?.defaultCurrency ?? '';
    const packQty = getSearchHitMultipackPackQty(product);
    const display = useProductPriceWeightDisplay(product, currencyCode, {
        packQty: packQty > 1 ? packQty : undefined,
    });
    const unitRow = {
        ...display.unitRow,
        weightText: formatMultipackWeightText(display.unitRow.weightText, packQty),
    };
    const productUrl = createProductUrl(product.c_url || '#');
    const productName = product.productName ?? product.productId;
    const productHighlight = product.representedProduct?.c_pimProductHighlights;
    const eatingList = product.representedProduct?.c_pimEatingStyles ?? [];
    const productType = product.representedProduct?.c_productType;
    const isFreeEventOrCourse = (productType === 'event' || productType === 'course') && product.price === 0;
    const currentFormatted = isFreeEventOrCourse ? t('eventCourse.freePrice') : display.currentFormatted;
    const listFormatted = isFreeEventOrCourse ? null : display.listFormatted;
    const isOnSale = isFreeEventOrCourse ? false : display.isOnSale;
    const pricePct = isFreeEventOrCourse ? 0 : display.pricePct;

    const { line: tileBasketLine } = useProductTileBasketLine(product, undefined);
    const omnibus = display.omnibus;

    const externalEventPrice =
        !commerceEnabled && (productType === 'event' || productType === 'course') ? (
            <ExternalEventPrice
                price={product.price}
                available={product.orderable}
                currency={currencyCode}
                className="body-copy-l-bold"
            />
        ) : null;

    const priceAndWeight = externalEventPrice ?? (
        <ProductTilePriceWeight
            layout={isHorizontal ? 'horizontal' : 'vertical'}
            t={t}
            isOnSale={isOnSale}
            currentFormatted={currentFormatted}
            listFormatted={listFormatted}
            unitRow={unitRow}
            perUnitLine={display.perUnitLine ?? null}
            pricePct={pricePct}
            invertTextColor={productType === 'event' && !useHorizontalEventCard}
            tileBasketLine={tileBasketLine}
            omnibus={omnibus}
        />
    );

    // Listing layout (Corsi/Eventi search + event/course PLPs) wins over the overlay event card.
    if (isVerticalEvent) {
        const { startDate, categoryName, isMaster } = getEventTileFields(product);
        const formattedStartDate = formatEventStartDateForTile(startDate, i18n.language);
        const formattedDateLine = formatEventDateLine({
            formattedDate: formattedStartDate,
            isMaster,
            t: tSearch,
        });
        const priceScreenReader = [
            listFormatted ? String(t('price.listPrice', { price: listFormatted })) : null,
            String(t('price.currentPrice', { price: currentFormatted })),
        ]
            .filter(Boolean)
            .join('. ');

        const priceBlock = externalEventPrice ?? (
            <ProductTilePriceLine
                screenReaderLabel={priceScreenReader}
                currentFormatted={currentFormatted}
                listFormatted={listFormatted}
                isOnSale={isOnSale}
                pricePct={pricePct}
                tileBasketLine={tileBasketLine}
            />
        );

        return withItemList(
            <ProductTileCustomVerticalEventCard
                ref={ref}
                className={className}
                product={product}
                productName={productName}
                productUrl={productUrl}
                effectiveImgAspectRatio={effectiveImgAspectRatio}
                formattedDateLine={formattedDateLine}
                categoryName={categoryName}
                priceBlock={priceBlock}
                handleProductClick={handleProductClick}
                {...divProps}
            />
        );
    }

    if (productType === 'event' && !useHorizontalEventCard) {
        return withItemList(
            <>
                <ProductTileCustomEventCard
                    ref={ref}
                    className={className}
                    product={product}
                    productName={productName}
                    brand={brand}
                    productUrl={productUrl}
                    effectiveImgAspectRatio={effectiveImgAspectRatio}
                    handleProductClick={onProductClick}
                    productHighlight={productHighlight}
                    eatingList={eatingList}
                    priceAndWeight={priceAndWeight}
                    t={t}
                    {...divProps}
                />
            </>
        );
    }

    if (isHorizontal) {
        return withItemList(
            <ProductTileCustomHorizontalCard
                ref={ref}
                className={className}
                product={product}
                productName={productName}
                brand={brand}
                productUrl={productUrl}
                effectiveImgAspectRatio={effectiveImgAspectRatio}
                handleProductClick={onProductClick}
                productHighlight={productHighlight}
                eatingList={eatingList}
                priceAndWeight={priceAndWeight}
                tileBasketLine={tileBasketLine}
                basketControlsDisabled={basketControlsDisabled}
                t={t}
                listFormatted={omnibus?.priorFormatted ? null : listFormatted}
                isOnSale={isOnSale}
                {...divProps}
            />
        );
    }

    return withItemList(
        <ProductTileCustomVerticalCard
            ref={ref}
            className={className}
            product={product}
            productName={productName}
            brand={brand}
            productUrl={productUrl}
            effectiveImgAspectRatio={effectiveImgAspectRatio}
            handleProductClick={onProductClick}
            productHighlight={productHighlight}
            eatingList={eatingList}
            priceAndWeight={priceAndWeight}
            tileBasketLine={tileBasketLine}
            basketControlsDisabled={basketControlsDisabled}
            {...divProps}
        />
    );
});

ProductTileCustom.displayName = 'ProductTileCustom';

export { ProductTileCustomSkeleton } from './skeleton';
export { ProductTileCustomGenericCard } from './product-tile-custom-generic-card';
export { ProductTileCustom };
export default ProductTileCustom;
